import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Switch,
  Textarea
} from '@heroui/react'
import { useEffect, useState } from 'react'
import { useSnapshot } from 'valtio'
import type { RadarConfig } from '../../../../types'
import { api } from '../../lib/api'
import { state, useRadarNav } from '../../lib/state'
import { SavedCheckmark } from '../../pages/Admin/usePersistedConfig'

interface EmbedSettingsProps {
  config: RadarConfig | null
  setConfig: (config: RadarConfig) => void
}

export const EmbedSettings = ({ config, setConfig }: EmbedSettingsProps) => {
  const { isLocal } = useSnapshot(state)
  const { radarId } = useRadarNav()
  const [copied, setCopied] = useState(false)
  const [webhookSecret, setWebhookSecret] = useState('')
  const [webhookSecretInput, setWebhookSecretInput] = useState('')
  const [saveStatus, setSaveStatus] = useState<
    'idle' | 'saving' | 'success' | 'error'
  >('idle')
  const [saveError, setSaveError] = useState<string | null>(null)
  const [webhookSecretSaved, setWebhookSecretSaved] = useState(false)
  const [apiUrl, setApiUrl] = useState<string>('')
  const [showRotateModal, setShowRotateModal] = useState(false)
  const [rotateStatus, setRotateStatus] = useState<
    'idle' | 'rotating' | 'success' | 'error'
  >('idle')
  const [rotateError, setRotateError] = useState<string | null>(null)
  const [copiedUrl, setCopiedUrl] = useState(false)
  const [embedUrl, setEmbedUrl] = useState<string>('')

  const handleEmbedToggleChange = async (checked: boolean) => {
    if (!config) return
    const updatedConfig = {
      ...config,
      toggles: {
        ...config.toggles,
        enableEmbed: checked
      }
    }
    setConfig(updatedConfig)
  }

  useEffect(() => {
    let cancelled = false
    if (!radarId) return

    if (isLocal) {
      // In dev mode, use the local mock endpoint
      setApiUrl('http://localhost:3000/api/dev/test-webtrigger')
      // Also set embedUrl for dev with current webhookSecret
      const devApiUrl = webhookSecret
        ? `http://localhost:3000/api/dev/test-webtrigger?secret=${encodeURIComponent(webhookSecret)}`
        : 'http://localhost:3000/api/dev/test-webtrigger'
      setEmbedUrl(
        `https://techradar-embed.forgedapps.com/radar/${radarId}?apiUrl=${encodeURIComponent(devApiUrl)}`
      )
      return
    }
    // In production, fetch the webhook URL from the backend
    api
      .getWebhookUrl()
      .then((url: string) => {
        if (!cancelled) {
          setApiUrl(url)
          // Compose the full embed URL
          const urlWithSecret = webhookSecret
            ? `${url}${url.includes('?') ? '&' : '?'}secret=${encodeURIComponent(webhookSecret)}`
            : url
          setEmbedUrl(
            `https://techradar-embed.forgedapps.com/radar/${radarId}?apiUrl=${encodeURIComponent(urlWithSecret)}`
          )
        }
      })
      .catch(() => {
        // fallback if error
        if (!cancelled) {
          setApiUrl('')
          setEmbedUrl('')
        }
      })
    // Fetch the saved webhook secret on mount
    api.getWebhookSecret().then(secret => {
      if (!cancelled && secret) {
        setWebhookSecret(secret)
        setWebhookSecretInput(secret)
      }
    })
    return () => {
      cancelled = true
    }
  }, [radarId, webhookSecret, isLocal])

  useEffect(() => {
    setWebhookSecretInput(webhookSecret)
  }, [webhookSecret])

  const generateEmbedCode = () => {
    if (!radarId) return ''

    // Always use production baseUrl for embed
    const baseUrl = 'https://techradar-embed.forgedapps.com'

    let embedUrl: string
    let devNote: string | null = null

    if (isLocal) {
      // Local dev: use the Vite dev server mock webtrigger endpoint for apiUrl
      const usedApiUrl = webhookSecret
        ? `http://localhost:3000/api/dev/test-webtrigger?secret=${encodeURIComponent(webhookSecret)}`
        : 'http://localhost:3000/api/dev/test-webtrigger'
      embedUrl = `${baseUrl}/radar/${radarId}?apiUrl=${encodeURIComponent(usedApiUrl)}`
      devNote = `\n<!-- Using local dev webtrigger endpoint: ${usedApiUrl} -->\n`
    } else {
      // Production: use the fetched API URL
      if (apiUrl) {
        const urlWithSecret = webhookSecret
          ? `${apiUrl}${apiUrl.includes('?') ? '&' : '?'}secret=${encodeURIComponent(webhookSecret)}`
          : apiUrl
        embedUrl = `${baseUrl}/radar/${radarId}?apiUrl=${encodeURIComponent(urlWithSecret)}`
      } else {
        // fallback if not loaded
        embedUrl = ''
      }
      devNote = null
    }

    return `<!-- Tech Radar Embed Code -->\n${devNote || ''}<iframe \n  src="${embedUrl}" \n  width="100%" \n  height="2100" \n  style="border: none; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);"\n  title="Tech Radar">\n</iframe>\n<!-- End Tech Radar Embed Code -->`
  }

  const handleCopy = () => {
    const embedCode = generateEmbedCode()
    navigator.clipboard.writeText(embedCode)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // Save webhook secret on blur
  const handleWebhookSecretBlur = async () => {
    if (saveStatus === 'saving') return
    setWebhookSecret(webhookSecretInput)
    setSaveStatus('saving')
    setSaveError(null)
    try {
      const result = (await api.setWebhookSecret(webhookSecretInput)) as {
        success: boolean
        error?: string
      }
      if (result.success) {
        setSaveStatus('success')
        setWebhookSecretSaved(true)
        setTimeout(() => setWebhookSecretSaved(false), 1200)
        setTimeout(() => setSaveStatus('idle'), 2000)
      } else {
        setSaveStatus('error')
        setSaveError(result.error || 'Unknown error')
      }
    } catch (e) {
      setSaveStatus('error')
      setSaveError(e instanceof Error ? e.message : String(e))
    }
  }

  // Clear webhook secret
  const handleClearWebhookSecret = async () => {
    setWebhookSecretInput('')
    setWebhookSecret('')
    setSaveStatus('saving')
    setSaveError(null)
    try {
      const result = (await api.setWebhookSecret('')) as {
        success: boolean
        error?: string
      }
      if (result.success) {
        setSaveStatus('success')
        setWebhookSecretSaved(true)
        setTimeout(() => setWebhookSecretSaved(false), 1200)
        setTimeout(() => setSaveStatus('idle'), 2000)
      } else {
        setSaveStatus('error')
        setSaveError(result.error || 'Unknown error')
      }
    } catch (e) {
      setSaveStatus('error')
      setSaveError(e instanceof Error ? e.message : String(e))
    }
  }

  // When rotating, also update embedUrl
  const handleRotateEmbedUrl = async () => {
    setRotateStatus('rotating')
    setRotateError(null)
    try {
      await api.rotateWebhook()
      // Fetch and update the new webhook URL
      const newUrl = (await api.getWebhookUrl()) as string
      setApiUrl(newUrl)
      const urlWithSecret = webhookSecret
        ? `${newUrl}${newUrl.includes('?') ? '&' : '?'}secret=${encodeURIComponent(webhookSecret)}`
        : newUrl
      setEmbedUrl(
        `https://techradar-embed.forgedapps.com/radar/${radarId}?apiUrl=${encodeURIComponent(urlWithSecret)}`
      )
      setRotateStatus('success')
      setTimeout(() => {
        setShowRotateModal(false)
        setRotateStatus('idle')
      }, 2000)
    } catch (e) {
      setRotateStatus('error')
      setRotateError(e instanceof Error ? e.message : String(e))
    }
  }

  // Early return if no config yet
  if (!config) return null

  return (
    <div>
      <h3
        className="text-lg font-medium text-[var(--ds-text)] mb-4"
        data-testid="settings-section-title-embed-radar"
      >
        Embed Radar
      </h3>

      {/* Embed Enable Toggle */}
      <div
        className="flex items-center justify-between p-3 mb-4 border border-[var(--ds-border)] rounded-[3px]"
        data-testid="enable-embed-toggle"
      >
        <div>
          <div
            className="text-sm text-[var(--ds-text)]"
            id="enable-embed-label"
          >
            Enable Embed
          </div>
          <div
            className="text-xs text-[var(--ds-text-subtle)]"
            id="enable-embed-description"
          >
            Allow this radar to be embedded in external websites
          </div>
        </div>
        <div
          style={{
            position: 'relative',
            display: 'inline-block',
            minWidth: 48
          }}
        >
          <Switch
            aria-label="Enable Embed"
            aria-describedby="enable-embed-description"
            isSelected={config?.toggles?.enableEmbed ?? false}
            onValueChange={handleEmbedToggleChange}
            data-testid="enable-embed-switch"
          />
        </div>
      </div>

      {/* Only show embed settings if embed is enabled */}
      {config?.toggles?.enableEmbed && (
        <div className="space-y-4" data-testid="embed-settings-content">
          {/* Embed URL display and rotate button */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <label
                htmlFor="embed-url"
                className="text-sm font-medium text-[var(--ds-text)]"
              >
                Embed URL
              </label>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="bordered"
                  className="text-[var(--ds-text)] rounded-[3px] h-8 px-3 border border-[var(--ds-border)] bg-transparent hover:bg-transparent"
                  onPress={() => {
                    navigator.clipboard.writeText(embedUrl)
                    setCopiedUrl(true)
                    setTimeout(() => setCopiedUrl(false), 2000)
                  }}
                  data-testid="copy-embed-url-button"
                >
                  {copiedUrl ? 'Copied!' : 'Copy URL'}
                </Button>
                <Button
                  size="sm"
                  variant="bordered"
                  className="text-[var(--ds-text)] rounded-[3px] h-8 px-3 border border-[var(--ds-border)] bg-transparent hover:bg-transparent"
                  onPress={() => setShowRotateModal(true)}
                  data-testid="rotate-embed-url-button"
                >
                  Rotate Embed URL
                </Button>
              </div>
            </div>
            <Input
              id="embed-url"
              value={embedUrl}
              readOnly
              onClick={e => {
                ;(e.target as HTMLInputElement).select()
              }}
              classNames={{
                input:
                  'text-sm font-mono group-data-[has-value=true]:text-[var(--ds-text)]',
                inputWrapper:
                  'border bg-[var(--ds-surface)] data-[hover=true]:bg-[var(--ds-surface)] focus-within:bg-[var(--ds-surface)]! rounded-[3px] data-[focused=true]:border-[var(--ds-border-focused)]!'
              }}
              data-testid="embed-url-input"
            />
          </div>
          {/* Rotate Embed URL Modal using Modal from @heroui/react */}
          <Modal
            isOpen={showRotateModal}
            onOpenChange={setShowRotateModal}
            data-testid="rotate-embed-url-modal"
            aria-labelledby="rotate-embed-url-modal-title"
            classNames={{
              base: 'heroui-modal-content',
              header: 'heroui-modal-header',
              body: 'heroui-modal-body',
              footer: 'heroui-modal-footer'
            }}
          >
            <ModalContent>
              <ModalHeader>
                <h3
                  id="rotate-embed-url-modal-title"
                  className="text-lg font-medium text-[var(--ds-text)]"
                  data-testid="rotate-embed-url-modal-title"
                >
                  Rotate Embed URL
                </h3>
              </ModalHeader>
              <ModalBody>
                <p className="mb-4">
                  Rotating the embed URL will generate a new unique URL.{' '}
                  <b>This will immediately invalidate existing embed codes</b>{' '}
                  and any pages using the old embed code will stop working until
                  updated.
                </p>
                <p className="text-sm text-[var(--ds-text-subtle)] mb-2">
                  Are you sure you want to rotate the embed URL?
                </p>
                {rotateStatus === 'error' && (
                  <p className="text-xs text-red-500 mt-1">{rotateError}</p>
                )}
                {rotateStatus === 'success' && (
                  <p className="text-xs text-green-600 mt-1">
                    Embed URL rotated! New URL is now active.
                  </p>
                )}
              </ModalBody>
              <ModalFooter>
                <Button
                  variant="bordered"
                  className="text-[var(--ds-text)] rounded-[3px] h-8 px-3 border border-[var(--ds-border)] bg-transparent hover:bg-transparent"
                  size="sm"
                  onPress={() => setShowRotateModal(false)}
                  isDisabled={rotateStatus === 'rotating'}
                >
                  Cancel
                </Button>
                <Button
                  variant="solid"
                  color="danger"
                  className="rounded-[3px] h-8 px-3"
                  size="sm"
                  onPress={handleRotateEmbedUrl}
                  isDisabled={
                    rotateStatus === 'rotating' || rotateStatus === 'success'
                  }
                >
                  {rotateStatus === 'rotating'
                    ? 'Rotating...'
                    : 'Rotate Embed URL'}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
          <div className="mb-4">
            <label
              htmlFor="embed-secret"
              className="block text-sm font-medium text-[var(--ds-text)] mb-1"
            >
              Embed Secret (optional)
            </label>
            <div
              style={{
                position: 'relative',
                display: 'inline-block',
                width: '100%'
              }}
            >
              <Input
                id="embed-secret"
                placeholder="Enter an embed secret for additional security"
                value={webhookSecretInput}
                onChange={e => setWebhookSecretInput(e.target.value)}
                onBlur={handleWebhookSecretBlur}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.currentTarget.blur()
                  }
                }}
                data-testid="embed-secret-input"
                classNames={{
                  input:
                    'text-sm group-data-[has-value=true]:text-[var(--ds-text)] pr-20', // increased right padding for clear button
                  inputWrapper:
                    'border bg-[var(--ds-surface)] data-[hover=true]:bg-[var(--ds-surface)] focus-within:bg-[var(--ds-surface)]! rounded-[3px] data-[focused=true]:border-[var(--ds-border-focused)]!'
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  right: 40,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 5
                }}
              >
                <SavedCheckmark isSaved={webhookSecretSaved} />
              </div>
              {/* Clear button */}
              {webhookSecretInput && (
                <button
                  type="button"
                  title="Clear embed secret"
                  aria-label="Clear embed secret"
                  onClick={handleClearWebhookSecret}
                  style={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 12,
                    color: '#666',
                    padding: '4px 8px',
                    borderRadius: '3px',
                    lineHeight: 1,
                    zIndex: 10
                  }}
                  data-testid="clear-embed-secret-button"
                >
                  Clear
                </button>
              )}
            </div>
            {/* Only show error if secret is non-empty and saveStatus is error */}
            {saveStatus === 'error' && webhookSecretInput && (
              <p className="text-xs text-red-500 mt-1">{saveError}</p>
            )}
          </div>
          <div className="flex justify-between items-center mb-2">
            <label
              htmlFor="embed-code"
              className="block text-sm font-medium text-[var(--ds-text)]"
            >
              Embed Code
            </label>
            <Button
              size="sm"
              variant="bordered"
              className="text-[var(--ds-text)] rounded-[3px] h-8 px-3 border border-[var(--ds-border)] bg-transparent hover:bg-transparent"
              onPress={handleCopy}
              data-testid="copy-embed-button"
            >
              {copied ? 'Copied!' : 'Copy Code'}
            </Button>
          </div>
          <Textarea
            id="embed-code"
            value={generateEmbedCode()}
            readOnly
            rows={8}
            data-testid="embed-code-textarea"
            classNames={{
              input:
                'text-sm font-mono group-data-[has-value=true]:text-[var(--ds-text)]',
              inputWrapper:
                'border bg-[var(--ds-surface)] data-[hover=true]:bg-[var(--ds-surface)] focus-within:bg-[var(--ds-surface)]! rounded-[3px] data-[focused=true]:border-[var(--ds-border-focused)]!'
            }}
          />
          {isLocal && (
            <p className="mt-2 text-sm text-[var(--ds-text-subtle)]">
              <strong>Dev mode:</strong> The embed code uses the local mock
              webtrigger endpoint (
              <code>
                http://localhost:3000/api/dev/test-webtrigger?radarId=...
              </code>
              ).
            </p>
          )}
          <p className="mt-2 text-sm text-[var(--ds-text-subtle)]">
            Copy this code and paste it into any HTML page where you want to
            embed the radar.
          </p>
        </div>
      )}

      {/* Show message when embed is disabled */}
      {!config?.toggles?.enableEmbed && (
        <div
          className="p-2 text-sm text-[var(--ds-text-subtle)]"
          data-testid="embed-disabled-message"
        >
          Enable embed functionality above to configure embed settings for this
          radar.
        </div>
      )}
    </div>
  )
}
